import React, { useContext, useMemo } from "react";
import { useNotify } from "react-admin";
import { useSWRConfig } from "swr";
import { CopyToClipboard } from "react-copy-to-clipboard";
import range from "lodash/range";
import uniqBy from "lodash/uniqBy";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";

import { shuffle, getRandomInt } from "../../helpers/methods";
import { useIsBusinessPlan } from "../../helpers/subscriptionPlan";
import { CACHE_CONTACTS } from "../Constant";
import { LIKE, LOVE, useFbReactionsHashMap } from "../DataHooks/useFbReactions";
import { useFBProfile } from "../DataHooks/fbPageProfile";
import { SET_LOADING, store } from "./store";
import { store as commentStore } from "./commentStore";
import useDebounce from "../UseDebounce";
import MediaCard from "./MediaCard";
import ConfettiWrapper from "./ConfettiWrapper";
import { DialogActions, DialogTitle } from "@mui/material";
import { createOrderItem } from "./sharedMethods";
import { getCookie } from "../../helpers/Cookies";

export const StyledFormWrapper = styled("form")(({ theme }) => ({
  "& > *": {
    margin: theme.spacing(1),
    width: "25ch",
  },
}));

export const StyledAppBar = styled(AppBar)(({ theme }) => ({
  position: "relative",
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  flex: 1,
}));

export const ContentWrapper = styled("div")(() => ({
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
}));

export const DottedDiv = styled("div")(({ theme }) => ({
  border: "10px dotted #000",
  width: "300px",
  height: "100px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(1),
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Picker = (props) => {
  const token = getCookie("token");
  const notify = useNotify();
  const { cache } = useSWRConfig();
  const { state, dispatch } = useContext(store);
  const { video } = state;
  const { data: thePage } = useFBProfile();
  const { data: reactionsHashMap } = useFbReactionsHashMap(
    thePage ? `${thePage.id}_${video.id}` : null
  );
  const isBusinessPlan = useIsBusinessPlan();
  const { state: commentState } = React.useContext(commentStore);

  const { page } = state;
  const {
    comments,
    // contactHashTable
  } = commentState;

  const [keyword, setKeyword] = React.useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [excludeBlacklistedContact, setExcludeBlacklistedContact] =
    React.useState(true);
  const [reactionLike, setReactionLike] = React.useState(false);
  const [reactionLove, setReactionLove] = React.useState(false);
  const [showTotalCandidate, setShowTotalCandidate] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [openQuickAddDialog, setOpenQuickAddDialog] = React.useState(false);
  const [prize, setPrize] = React.useState({
    description: "",
    quantity: 1,
    price: 0,
  });

  const handleClose = () => {
    setOpen(false);
  };
  const handleQuickAddClose = React.useCallback(() => {
    setOpenQuickAddDialog(false);
  }, []);

  const [winners, setWinners] = React.useState([]);
  const [quantity, setQuantity] = React.useState(1);
  const onChangeKeyword = React.useCallback(
    (e) => {
      setKeyword(e.target.value);
    },
    [setKeyword]
  );
  const onChangeQuantity = React.useCallback(
    (e) => {
      setQuantity(e.target.value);
    },
    [setQuantity]
  );

  const winnersListText = `---WINNERS LIST---\n${winners
    .map((c) => c.from.name)
    .join(", ")}`;

  const candidates = useMemo(() => {
    if (debouncedKeyword) {
      const k = debouncedKeyword.toUpperCase();
      const allMatches = comments
        .filter((c) => c.from.id !== page.id)
        .filter((c) => {
          const msg = c.message.toUpperCase();
          return msg.indexOf(k) !== -1;
        })
        .filter((c) => {
          const cacheData = cache.get(`${CACHE_CONTACTS}/${c.from.id}`);
          if (!excludeBlacklistedContact) {
            return true;
          } else if (!cacheData || !cacheData.data) {
            return true; //new customer
          }
          return !cacheData.data.isBlacklisted;
        })
        .filter((c) => {
          // This code snippet filters an array based on conditions related to reaction types like 'LIKE' and 'LOVE'. It checks if the reactions are 'LIKE' or 'LOVE' for each item and filters the array accordingly.
          if (reactionLike || reactionLove) {
            const audienceReaction = reactionsHashMap[c.from.id];
            if (audienceReaction && audienceReaction.type) {
              const { type } = audienceReaction;
              return (
                (reactionLike && type === LIKE) ||
                (reactionLove && type === LOVE)
              );
            }
            return false;
          }
          return true;
        });

      const allUniqueMatches = uniqBy(allMatches, (c) => c.from.id);
      return allUniqueMatches;
    }
    return [];
  }, [
    cache,
    comments,
    debouncedKeyword,
    excludeBlacklistedContact,
    page.id,
    reactionLike,
    reactionLove,
    reactionsHashMap,
  ]);

  const drawWinner = React.useCallback(() => {
    const nbShuffles = range(getRandomInt(100) + 1);
    const newArr = nbShuffles.reduce((acc, cur) => shuffle(acc), candidates);
    const newList = newArr.slice(0, quantity);
    setWinners(newList);
  }, [candidates, quantity]);

  const handleAddOrderitem = React.useCallback(async () => {
    if (!winners || winners.length === 0) {
      notify("No winners to add", { type: "warning" });
      return;
    }

    dispatch({ type: SET_LOADING, payload: true });
    const orders = winners.map((w) => ({
      description: `${prize.description} x${prize.quantity}`,
      quantity: prize.quantity,
      price: prize.price,
      psid: w.from.id,
      customerName: w.from.name,
      commentId: w.id,
    }));
    const payload = { data: { orders } };

    try {
      await createOrderItem({ token, payload });
      notify(`Created for ${winners.map((w) => w.from.name).join(", ")}`);
    } catch (err) {
      console.error("Failed to create order items", err);
      notify("Failed to create order items", { type: "warning" });
    }

    dispatch({ type: SET_LOADING, payload: false });
    handleQuickAddClose();
  }, [winners, prize, token, notify, dispatch, handleQuickAddClose]);

  return (
    <div>
      <StyledFormWrapper noValidate autoComplete="off">
        <TextField
          label="keyword"
          required
          helperText={
            <span>
              {`# of possibilities ${candidates.length}`}
              {candidates.length > 0 && (
                <CheckIcon sx={{ color: "green" }} fontSize="small" />
              )}
            </span>
          }
          onChange={onChangeKeyword}
          value={keyword}
          size="small"
          fullWidth={false}
        />
        <TextField
          margin="dense"
          label="number of pick"
          required
          onChange={onChangeQuantity}
          value={quantity}
          type="number"
          size="small"
          fullWidth={false}
        />
        <br />
        <FormControlLabel
          control={
            <Checkbox
              checked={excludeBlacklistedContact}
              onChange={(e) => setExcludeBlacklistedContact(e.target.checked)}
            />
          }
          label="Exclude Blacklisted"
        />
        <br />
        <FormControlLabel
          control={
            <Checkbox
              checked={showTotalCandidate}
              onChange={(e) => setShowTotalCandidate(e.target.checked)}
            />
          }
          label="Show total candidate"
        />
        {isBusinessPlan && (
          <FormControlLabel
            control={
              <Checkbox
                checked={reactionLike}
                onChange={(e) => setReactionLike(e.target.checked)}
              />
            }
            label={<ThumbUpIcon sx={{ color: "#1877F2" }} />}
          />
        )}

        {!isBusinessPlan && (
          <FormControlLabel
            control={<Checkbox disabled />}
            label={
              <span>
                <ThumbUpIcon disabled />
                (Only business plan)
              </span>
            }
          />
        )}

        {isBusinessPlan && (
          <FormControlLabel
            control={
              <Checkbox
                checked={reactionLove}
                onChange={(e) => setReactionLove(e.target.checked)}
              />
            }
            label={<FavoriteIcon sx={{ color: "red" }} />}
          />
        )}

        {!isBusinessPlan && (
          <FormControlLabel
            control={<Checkbox disabled />}
            label={
              <span>
                <FavoriteIcon disabled />
                (Only business plan)
              </span>
            }
          />
        )}

        <br />
        <TextField
          label={`Names(${winners.length})`}
          multiline
          rows={4}
          value={winnersListText}
        />

        <Button
          variant="contained"
          color="secondary"
          disabled={!quantity || quantity < 1 || candidates.length < 1}
          onClick={() => {
            setOpen(true);
          }}
        >
          Preview
        </Button>
        <CopyToClipboard text={winnersListText}>
          <Button variant="outlined">Copy</Button>
        </CopyToClipboard>
        <Button
          variant="contained"
          onClick={() => {
            setWinners([]);
          }}
        >
          Clear list
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={winners.length === 0}
          onClick={() => setOpenQuickAddDialog(true)}
        >
          Add as orderitem
        </Button>
      </StyledFormWrapper>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <StyledAppBar>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <StyledTitle variant="h6">Comment picker</StyledTitle>
          </Toolbar>
        </StyledAppBar>
        <DialogContent>
          <Grid
            container
            spacing={2}
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
            columns={{ xs: 3, md: 9 }}
          >
            {winners.map((value) => (
              <Grid key={value.from.id} size={{ xs: 6, md: 4, lg: 3, xl: 2 }}>
                <MediaCard value={value} />
              </Grid>
            ))}
          </Grid>

          <ContentWrapper>
            {showTotalCandidate && (
              <DottedDiv>
                <h1>{`Candidates: ${candidates.length}`}</h1>
              </DottedDiv>
            )}
            <br />
            <Button variant="contained" onClick={drawWinner}>
              {`Draw max ${quantity}`}
            </Button>
          </ContentWrapper>
          {winners.length > 0 && <ConfettiWrapper />}
        </DialogContent>
      </Dialog>
      <Dialog open={openQuickAddDialog} onClose={handleQuickAddClose}>
        <DialogTitle>Quick Add Prize</DialogTitle>
        <DialogContent dividers>
          <p>Winners</p>
          {winners.length > 0 ? (
            <ul style={{ paddingLeft: 20 }}>
              {winners.map((w, index) => (
                <li key={w.id || index}>{w.from.name}</li>
              ))}
            </ul>
          ) : (
            <p>No winners yet.</p>
          )}
          <TextField
            fullWidth
            margin="normal"
            label="Prize"
            variant="outlined"
            value={prize.description}
            onChange={(e) =>
              setPrize((prev) => ({ ...prev, description: e.target.value }))
            }
          />

          <TextField
            fullWidth
            margin="normal"
            label="Price"
            type="number"
            variant="outlined"
            value={prize.price}
            onChange={(e) =>
              setPrize((prev) => ({
                ...prev,
                price: parseFloat(e.target.value) || 0,
              }))
            }
          />

          <TextField
            fullWidth
            margin="normal"
            label="Quantity"
            type="number"
            variant="outlined"
            value={prize.quantity}
            onChange={(e) =>
              setPrize((prev) => ({
                ...prev,
                quantity: parseInt(e.target.value, 10) || 1,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddOrderitem}
          >
            Add
          </Button>
          <Button onClick={handleQuickAddClose} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Picker;
